import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'

import {
  Card,
  Icon,
  Rating,
  InfoOutlined,
  classnames,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  string,
  bool,
  number,
  arrayOf,
  oneOf,
  shape,
} from 'prop-types'

import PreviewEditButton from 'Pods/ItemPage/Components/PreviewEditButton/PreviewEditButton'
import WishlistButton from 'Pods/ItemPage/Components/WishlistButton/WishlistButton'
import { usePreviewMode } from 'Pods/ItemPage/context/PreviewModeContext'
import { useIs3dPrintModel } from 'Pods/ItemPage/TopSection/hooks/useTopSectionProps'
import { availableLicenses } from 'Pods/Shared/availableLicenses'
import useGon from 'Pods/Shared/Hooks/useGon'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import i18n from 'Pods/Shared/I18n'

import checkDownloadAvailabilityAndGetUrl from 'Utils/checkDownloadAvailabilityAndGetUrl'
import downloadLimitMessage from 'Utils/downloadLimitMessage'
import { sendExperimentEvent } from 'Utils/Experiments'
import formatPrice from 'Utils/formatPrice'
import resolveProductPricing from 'Utils/ResolveProductPricing'
import showErrorNotification from 'Utils/showErrorNotification'
import { EMPTY_ARRAY } from 'Utils/stableDefaults'

import AddToCartButton from './Components/AddToCartButton/AddToCartButton'
import BuyNowButton from './Components/BuyNowButton/BuyNowButton'
import CommentButton from './Components/CommentButton/CommentButton'
import FreeDownloadButton from './Components/FreeDownloadButton/FreeDownloadButton'
import LicenseDropdownLoader from './Components/LicenseDropdownLoader/LicenseDropdownLoader'
import LicenseModalLoader from './Components/LicenseModalLoader/LicenseModalLoader'
import ModelNotAvailableButton from './Components/ModelNotAvailableButton/ModelNotAvailableButton'
import MyPurchasesButton from './Components/MyPurchasesButton/MyPurchasesButton'
import PricingTag from './Components/PricingTag/PricingTag'
import ProceedToCheckoutButton from './Components/ProceedToCheckoutButton/ProceedToCheckoutButton'
import ReviewButton from './Components/ReviewButton/ReviewButton'
import deriveSubscriptionWidgetTracking from './Components/SubscriptionPurchase/deriveSubscriptionWidgetTracking'
import useItemPageSubscriptionTracking from './Components/SubscriptionPurchase/hooks/useItemPageSubscriptionTracking'
import PURCHASE_OPTION from './Components/SubscriptionPurchase/purchaseOptions'
import SubscribeButton from './Components/SubscriptionPurchase/SubscribeButton/SubscribeButton'
import SubscriberStatus from './Components/SubscriptionPurchase/SubscriberStatus/SubscriberStatus'
import SubscriptionPurchase from './Components/SubscriptionPurchase/SubscriptionPurchase'
import useBuyNowCleanup from './hooks/useBuyNowCleanup'

const DEFAULT_MOCKED_PRICING_DATA = {
  price: null,
  originalPrice: null,
  discount: null,
  discountAmount: null,
  ribbon: null,
}

// editorial is omitted: it is already a No AI variant in availableLicenses (allowAi: false)
// and shares its feature set with editorial_no_ai, so remapping is a no-op.
// editorial_royalty_free maps to royalty_free_no_ai (not editorial_no_ai) because the variant
// uses RF feature set; mapping to editorial_no_ai would also drop Remixing/Commercial rights.
const FORCE_NO_AI_LICENSE_MAP = {
  royalty_free: 'royalty_free_no_ai',
  editorial_royalty_free: 'royalty_free_no_ai',
  custom: 'custom_no_ai',
}

function PricingArea({
  title,
  price,
  license,
  customLicense = '',
  publishedAiFriendly = false,
  free,
  reviewsCount,
  ratingScore,
  badges = EMPTY_ARRAY,
  productId,
  locale = 'en',
  mockedPricingData = DEFAULT_MOCKED_PRICING_DATA,
  mockedIsInCart = false,
  mockedIsProductOwned = false,
  extractingCoreStyles = false,
  subscriptionDownloadsPerMonth,
  subscriptionCatalogValue,
  subscriptionTierBand = 'essentials',
  subscriptionBullets = EMPTY_ARRAY,
  subscriptionSubscribed = false,
  subscriptionPricePerModel,
  subscriptionPerModelLabel,
  subscriptionFromPriceLabel,
  subscriptionPaymentFormUrl = '/profile/subscription/payment-form',
  subscriptionUpgradeUrl = '/profile/subscription/upgrade',
  subscriptionV17CatalogEnabled = false,
  planName,
}) {
  const is3dPrintModel = useIs3dPrintModel()
  const { isPreview } = usePreviewMode()
  const [isRestricted, setIsRestricted] = useState()
  const [isDropDownActive, setIsDropDownActive] = useState(false)
  const [isProductOwned, setIsProductOwned] = useState(extractingCoreStyles ? mockedIsProductOwned : false)
  const [isFreeItemDownloaded, setIsFreeItemDownloaded] = useState(false)
  const [isInCart, setIsInCart] = useState(extractingCoreStyles ? mockedIsInCart : false)
  const [productPricing, setProductPricing] = useState(extractingCoreStyles ? mockedPricingData : {
    price,
    originalPrice: price,
    discount: null,
    discountAmount: null,
    ribbon: null,
  })
  const [isLicenseModalActive, setIsLicenseModalActive] = useState(false)
  const [userSelectedOption, setUserSelectedOption] = useState(null)
  const [isCheckingDownload, setIsCheckingDownload] = useState(false)
  const [isDownloadLimitReached, setIsDownloadLimitReached] = useState(false)

  const pageConfig = usePageConfig()
  const gon = useGon()
  const { restricted } = gon || {}
  const isLoggedIn = pageConfig?.isCurrentUser || false

  const {
    enableBuyNow,
    isActiveSubscriber,
    subscriptionTier,
    subscriptionScheduledForCancellation,
    subscriptionFlowEnabled,
    forceNoAiLicense = false,
    itemPagePreselectOnetimeExperimentValue,
  } = pageConfig || {}

  // item_page_preselect_onetime: the test arm preselects the one-time option, control keeps the
  // subscription. Derived rather than seeded into state so the first frame the selector paints already
  // carries the assignment (the widget itself only renders once pageConfig resolves), and a manual
  // choice always wins from then on. Scoped to non-subscribers to match the exposure cohort below: an
  // Essentials subscriber on a Premium-band item still sees the selector, and the upgrade CTA renders
  // only on the subscription option, so preselecting one-time would bury a path we never measure.
  const preselectOnetime = itemPagePreselectOnetimeExperimentValue === true && !isActiveSubscriber
  const defaultOption = preselectOnetime ? PURCHASE_OPTION.ONETIME : PURCHASE_OPTION.SUBSCRIPTION
  const purchaseOption = userSelectedOption ?? defaultOption

  const displayLicenseId = forceNoAiLicense
    ? (FORCE_NO_AI_LICENSE_MAP[license] || license)
    : license

  const licenseConfig = availableLicenses.find((item) => item.id === displayLicenseId) || {
    id: displayLicenseId,
    title: 'License',
    allowAi: false,
    features: [],
  }
  const isCustomLicense = !forceNoAiLicense && ['custom', 'custom_no_ai'].includes(license)

  const isSubscriptionEnabled = subscriptionFlowEnabled && !extractingCoreStyles

  const premiumExposed = !!pageConfig?.premiumExposed
  const isPremiumBandItem = subscriptionV17CatalogEnabled && subscriptionTierBand === 'premium'

  // An active subscription covers this item when the viewer is Premium (covers both bands) or the
  // item is not Premium-band. Mirrors Subscription#covers_item? on the backend, computed from the
  // real catalog band (a fact about entitlement, independent of the rollout experiment), so an
  // Essentials subscriber viewing a Premium-band item is NOT covered and must upgrade or buy it once.
  const subscriptionCoversItem =
    isActiveSubscriber && (subscriptionTier === 'premium' || !isPremiumBandItem)

  // Premium buyer-surface gate (subscription_v1_7 kill switch): it gates only the OFFER of Premium
  // (upsell / subscribe / upgrade). It must NEVER hide an existing subscriber's entitlement, so an
  // active subscription that already covers the item bypasses the gate. With the switch off, users
  // who don't cover the item get the Essentials-only world (no Premium offer). Resolved from
  // pageConfig client-side (fast page), riding the same hydration boundary as the rest of the
  // subscription UI (subscriptionFlowEnabled).
  const premiumBandExposed = !isPremiumBandItem || premiumExposed || subscriptionCoversItem

  // Every term here resolves with /load-services.js or from an SSR prop. isProductOwned and
  // isRestricted are kept out so the experiment cohort below can reuse this without waiting for
  // /products/<slug>.js - see the exposure effect.
  const subscriptionOfferable =
    isSubscriptionEnabled &&
    subscriptionSubscribed &&
    premiumBandExposed &&
    !free &&
    !isPreview

  const canShowSubscriptionUI = subscriptionOfferable && !isProductOwned && !isRestricted

  // subscription_v1_7_catalog gates whether the Premium tier exists at all; premiumBandExposed
  // gates the per-user rollout. With the catalog flag off the server already drops Premium-band
  // items from the offering (subscription_subscribed_for_ui? -> eligible_for_subscription?), and
  // a control user collapses the item to the Essentials band with no Premium tier in the redirect.
  const effectiveTierBand =
    premiumBandExposed && subscriptionV17CatalogEnabled ? subscriptionTierBand : 'essentials'

  // An active Essentials subscriber on a Premium-band item upgrades their plan (goes to the
  // upgrade checkout) instead of starting a fresh subscription at the payment form.
  const isEssentialsToPremiumUpgrade =
    isActiveSubscriber && subscriptionTier === 'essentials' && effectiveTierBand === 'premium'

  const showSubscriptionFlow = canShowSubscriptionUI && !subscriptionCoversItem
  const showSubscriberBenefits = canShowSubscriptionUI && subscriptionCoversItem

  const showSubscriptionLicense = subscriptionCoversItem && subscriptionSubscribed
  const effectiveLicenseConfig = showSubscriptionLicense
    ? availableLicenses.find((item) => item.id === 'royalty_free_no_ai')
    : licenseConfig
  const effectiveLicense = showSubscriptionLicense ? 'royalty_free_no_ai' : displayLicenseId
  const showAiContactNote = forceNoAiLicense && publishedAiFriendly && !showSubscriptionLicense

  useEffect(() => {
    if (!pageConfig) {
      return
    }

    const { cart, product, isCurrentUser } = pageConfig
    const { itemIds = [] } = cart || {}
    const { isPurchased, id } = product || {}

    if (extractingCoreStyles) {
      return
    }

    if (cart) {
      setIsInCart(itemIds.includes(productId))
    }

    if (isPurchased && free) {
      setIsFreeItemDownloaded(true)
    } else if (isPurchased) {
      setIsProductOwned(true)
    }

    if (product && cart) {
      const pricingData = resolveProductPricing({
        cart,
        attributes: product,
        id,
        isCurrentUser,
      })
      setProductPricing(pricingData)
    }
  }, [pageConfig, productId, free])

  useEffect(() => {
    setIsRestricted(restricted)
  }, [restricted])

  useEffect(() => {
    const removeCartItemId = (data) => {
      const { attributes } = data || {}
      const { itemId } = attributes || {}
      if (itemId === productId) {
        setIsInCart(false)
      }
    }

    App.events.subscribe('app:cart:item:remove', removeCartItemId)

    return () => {
      App.events.unsubscribe('app:cart:item:remove', removeCartItemId)
    }
  }, [productId])

  // subscriptionOfferable resolves with the payload that also unlocks the action buttons
  // (renderActionButtons paints a placeholder until pageConfig lands), so nobody can act on the page
  // before being counted. The owned/restricted terms it excludes arrive only with /products/<slug>.js,
  // a second round trip the fastest buyers never stayed for: waiting for it dropped them from one arm
  // (SRM on exp_19g6nmsrj84te). Such a view shows no selector under either default, so enrolling it
  // dilutes both arms symmetrically instead of dropping the fast buyers from one.
  const isExperimentEligible = subscriptionOfferable && !isActiveSubscriber
  const hasReportedExposure = useRef(false)

  useEffect(() => {
    // undefined means the client config has not landed yet; null means it landed and the user is
    // unbucketed. Latching on undefined would suppress the exposure entirely.
    const isArmResolved = itemPagePreselectOnetimeExperimentValue !== undefined

    if (!isExperimentEligible || !isArmResolved || hasReportedExposure.current) {
      return
    }

    hasReportedExposure.current = true
    sendExperimentEvent('item_page_preselect_onetime', itemPagePreselectOnetimeExperimentValue)
  }, [isExperimentEligible, itemPagePreselectOnetimeExperimentValue])

  const subscriptionTracking = useMemo(
    () => deriveSubscriptionWidgetTracking({
      price, isLoggedIn, isActiveSubscriber, subscriptionTier, subscriptionTierBand: effectiveTierBand,
      subscriptionScheduledForCancellation,
    }),
    [price, isLoggedIn, isActiveSubscriber, subscriptionTier, effectiveTierBand, subscriptionScheduledForCancellation]
  )
  const { trackWidgetVisible, trackCtaClick } = useItemPageSubscriptionTracking({
    itemId: productId,
    trackingPayload: subscriptionTracking,
  })

  useBuyNowCleanup(productId, enableBuyNow)

  const handleOpenLicenseModal = () => {
    setIsLicenseModalActive(true)
  }

  const handleCloseLicenseModal = () => {
    setIsLicenseModalActive(false)
  }

  const handleToggleDropDown = useCallback(() => {
    if (isCustomLicense) {
      handleOpenLicenseModal()
      return
    }

    setIsDropDownActive(state => !state)
  }, [isCustomLicense])

  const handleRatingClick = useCallback((event) => {
    event.preventDefault()
    window.dispatchEvent(new CustomEvent('open-review-modal', {
      detail: { filterBy: 'positive' },
    }))
  }, [])

  const handleFreeDownloadClick = async () => {
    if (isCheckingDownload || isDownloadLimitReached) {
      return
    }

    if (!isLoggedIn) {
      App.events.publish('app:login:open')
      return
    }

    const path = showSubscriberBenefits ? 'subscription-download-page' : 'download-page'
    const destinationUrl = `/items/${productId}/${path}`

    setIsCheckingDownload(true)

    try {
      const response = await checkDownloadAvailabilityAndGetUrl({
        itemId: productId,
        destinationUrl,
      })

      if (response.status === 'ok' && response.downloadUrl) {
        window.location.href = response.downloadUrl
        return
      }

      if (response.status === 'limit_reached') {
        setIsDownloadLimitReached(true)
        showErrorNotification(downloadLimitMessage(response))
        return
      }

      showErrorNotification('Something went wrong, please try again')
    } catch {
      showErrorNotification('Something went wrong, please try again')
    } finally {
      setIsCheckingDownload(false)
    }
  }

  const handleSubscriberDownloadClick = () => {
    trackCtaClick('download')
    handleFreeDownloadClick()
  }

  const renderPriceSection = () => {
    if (free) return null

    const hasDiscount = productPricing?.discount > 0
    const isSaleOff = productPricing?.ribbon === 'saleOff'
    const isCLD = productPricing?.ribbon === 'CLD'
    const shouldShowDiscount = (isSaleOff || isCLD) && hasDiscount

    return (
      <div className="pricing-area__price-container">
        <span className="pricing-area__price-text">
          {formatPrice(productPricing?.price)}
        </span>
        {shouldShowDiscount && (
          <div className="pricing-area__price-discount-container">
            <span className="pricing-area__price-discount-text">
              {formatPrice(productPricing.originalPrice)}
            </span>
            <span className="pricing-area__discount-percentage">
              -{Math.round(productPricing.discount * 100)}%
            </span>
          </div>
        )}
      </div>
    )
  }

  const renderLicenseText = () => {
    if (!effectiveLicenseConfig.allowAi) {
      return (
        <span
          className="pricing-area__license-text"
          onClick={handleToggleDropDown}
        >
          {effectiveLicenseConfig.title}{' '}
          <span className="pricing-area__license-text-ai">(no AI)</span>
        </span>
      )
    }

    return (
      <span className="pricing-area__license-text">
        {effectiveLicenseConfig.title}
      </span>
    )
  }

  const handleAddToCartSuccess = () => {
    setIsInCart(true)
  }

  const renderActionButtons = () => {
    if (!pageConfig && !extractingCoreStyles) {
      return <div className="pricing-area__actions-placeholder" />
    }

    if (isPreview) {
      return (
        <>
          <WishlistButton productId={productId} disabled />
          {free ? (
            <FreeDownloadButton disabled />
          ) : (
            <AddToCartButton productId={productId} disabled />
          )}
        </>
      )
    }

    if (isFreeItemDownloaded) {
      return (
        <>
          <FreeDownloadButton
            onClick={handleFreeDownloadClick}
            isDownloaded
            disabled={isCheckingDownload || isDownloadLimitReached}
            isLoading={isCheckingDownload}
          />
          <CommentButton />
        </>
      )
    }

    if (isProductOwned) {
      return (
        <>
          <MyPurchasesButton />
          <ReviewButton />
        </>
      )
    }

    if (free) {
      return (
        <>
          {extractingCoreStyles && <div className="pricing-area__actions-placeholder" />}
          <FreeDownloadButton
            onClick={handleFreeDownloadClick}
            disabled={isCheckingDownload || isDownloadLimitReached}
            isLoading={isCheckingDownload}
          />
        </>
      )
    }

    if (isRestricted) {
      return (
        <>
          <WishlistButton productId={productId} />
          <ModelNotAvailableButton />
        </>
      )
    }

    if (showSubscriberBenefits) {
      return (
        <FreeDownloadButton
          onClick={handleSubscriberDownloadClick}
          label="Download"
          showIcon
          disabled={isCheckingDownload || isDownloadLimitReached}
          isLoading={isCheckingDownload}
        />
      )
    }

    if (showSubscriptionFlow && purchaseOption === PURCHASE_OPTION.SUBSCRIPTION) {
      return (
        <SubscribeButton
          paymentFormPath={isEssentialsToPremiumUpgrade ? subscriptionUpgradeUrl : subscriptionPaymentFormUrl}
          label={i18n.t(
            isEssentialsToPremiumUpgrade
              ? 'subscription_item_page.upgrade_cta'
              : 'subscription_item_page.subscribe_cta',
            { ns: 'rails' },
          )}
          onClick={(navigate) => trackCtaClick(isEssentialsToPremiumUpgrade ? 'upgrade' : 'new_subscribe', navigate)}
        />
      )
    }

    if (enableBuyNow) {
      if (isInCart) {
        return <ProceedToCheckoutButton />
      }

      return (
        <>
          <AddToCartButton
            productId={productId}
            onSuccess={handleAddToCartSuccess}
            variant="secondary"
          />
          <BuyNowButton productId={productId} />
        </>
      )
    }

    return (
      <>
        <WishlistButton productId={productId} />
        {isInCart ? (
          <ProceedToCheckoutButton />
        ) : (
          <AddToCartButton
            productId={productId}
            onSuccess={handleAddToCartSuccess}
          />
        )}
      </>
    )
  }

  return (
    <Card hasBorder isRounded customClass="pricing-area">
      {badges.length > 0 && (
        <div className="pricing-area__header">
          {badges.map((badge) => (
            <PricingTag key={badge} badge={badge} />
          ))}
        </div>
      )}
      <div className="pricing-area__content">
        <div className="pricing-area__title-wrapper">
          <h1 className="pricing-area__title">{title}</h1>
          <PreviewEditButton className="preview-edit-button--title" />
        </div>
        {!free && reviewsCount > 0 && (
          <div className="pricing-area__rating-container">
            <a
              href="#reviews"
              className="pricing-area__rating-link"
              onClick={handleRatingClick}
            >
              <Rating
                id="pricing-area-rating"
                initialValue={ratingScore}
                numberOfItems={5}
                size="small"
                trail={reviewsCount === 1 ? `(1 review)` : `(${reviewsCount} reviews)`}
                isCheckboxHidden
                className="pricing-area__rating"
                ariaLabelPrefix={is3dPrintModel ? '3D Print Model rating' : 'Model rating'}
                isFractional
              />
            </a>
          </div>
        )}

        {(showSubscriptionFlow || showSubscriberBenefits) ? (
          <>
            <div
              className={classnames(
                'pricing-area__license-container',
                free && 'pricing-area__license-container--free-item'
              )}
            >
              {renderLicenseText()}
              <div className="pricing-area__license-container-icon">
                <Icon
                  svgComponent={InfoOutlined}
                  color="dark"
                  className="pricing-area__license-icon"
                  onClick={handleToggleDropDown}
                />
              </div>
              <LicenseDropdownLoader
                licenseConfig={effectiveLicenseConfig}
                onClose={handleToggleDropDown}
                isActive={isDropDownActive}
                showAiContactNote={showAiContactNote}
                locale={locale}
                onLicenseClick={handleOpenLicenseModal}
              />
            </div>
            {showSubscriberBenefits ? (
              <SubscriberStatus productPrice={formatPrice(productPricing?.price)} />
            ) : (
              <SubscriptionPurchase
                planName={planName}
                subscriptionTierBand={effectiveTierBand}
                subscriptionBullets={subscriptionBullets}
                subscriptionPricePerModel={subscriptionPricePerModel}
                subscriptionPerModelLabel={subscriptionPerModelLabel}
                subscriptionFromPriceLabel={subscriptionFromPriceLabel}
                subscriptionDownloadsPerMonth={subscriptionDownloadsPerMonth}
                subscriptionCatalogValue={subscriptionCatalogValue}
                productPrice={formatPrice(productPricing?.price)}
                productOriginalPrice={formatPrice(productPricing?.originalPrice)}
                hasDiscount={productPricing?.discount > 0}
                selectedOption={purchaseOption}
                onOptionChange={setUserSelectedOption}
                onImpression={trackWidgetVisible}
              />
            )}
          </>
        ) : (
          <>
            {renderPriceSection()}
            <div
              className={classnames(
                'pricing-area__license-container',
                free && 'pricing-area__license-container--free-item'
              )}
            >
              {renderLicenseText()}
              <div className="pricing-area__license-container-icon">
                <Icon
                  svgComponent={InfoOutlined}
                  color="dark"
                  className="pricing-area__license-icon"
                  onClick={handleToggleDropDown}
                />
              </div>
              <LicenseDropdownLoader
                licenseConfig={effectiveLicenseConfig}
                onClose={handleToggleDropDown}
                isActive={isDropDownActive}
                showAiContactNote={showAiContactNote}
                locale={locale}
                onLicenseClick={handleOpenLicenseModal}
              />
            </div>
          </>
        )}
      </div>
      <div className="pricing-area__actions">
        {renderActionButtons()}
      </div>

      <LicenseModalLoader
        isActive={isLicenseModalActive}
        onClose={handleCloseLicenseModal}
        license={effectiveLicense}
        customLicense={customLicense}
        licenseTitle={effectiveLicenseConfig.title}
      />
    </Card>
  )
}

PricingArea.propTypes = {
  title: string.isRequired,
  price: string.isRequired,
  license: oneOf([
    'custom',
    'custom_no_ai',
    'editorial',
    'editorial_no_ai',
    'editorial_royalty_free',
    'royalty_free',
    'royalty_free_no_ai',
  ]).isRequired,
  customLicense: string,
  publishedAiFriendly: bool,
  locale: string,
  free: bool.isRequired,
  reviewsCount: number.isRequired,
  ratingScore: number.isRequired,
  badges: arrayOf(oneOf([
    'BEST PRICE GUARANTEED',
    'NEW',
    'TOP SELLING',
    'QUALITY VERIFIED',
    '3D PRINT VERIFIED',
    'TOP RATED',
    'AI GENERATED',
    'DESIGNER\'S PICK',
  ])),
  productId: number.isRequired,
  mockedPricingData: shape({
    price: number,
    originalPrice: number,
    discount: number,
    discountAmount: number,
    ribbon: string,
  }),
  mockedIsInCart: bool,
  mockedIsProductOwned: bool,
  extractingCoreStyles: bool,
  subscriptionDownloadsPerMonth: number.isRequired,
  subscriptionCatalogValue: string.isRequired,
  subscriptionTierBand: string,
  subscriptionBullets: arrayOf(string),
  subscriptionSubscribed: bool,
  subscriptionPricePerModel: string.isRequired,
  subscriptionPerModelLabel: string.isRequired,
  subscriptionFromPriceLabel: string.isRequired,
  subscriptionPaymentFormUrl: string,
  subscriptionUpgradeUrl: string,
  subscriptionV17CatalogEnabled: bool,
  planName: string.isRequired,
}

export default PricingArea
